import React from "react";

// Dashed ephemeris lines + anchor dot + collision-adjusted label pill per astro line.
export function AstroLinesLayer({ lines }) {
  return lines.map((l) => {
    const fs = 9;
    const labelW = l.label.length * fs * 0.6 + 8;
    const labelH = fs + 4;
    const la = l.labelAnchor;
    const showConnector = Math.abs(la.y - (l.anchor.y - 6)) > 3 || Math.abs(la.x - (l.anchor.x + 8)) > 3;
    return (
      <g key={l.key} data-testid={`astro-line-svg-${l.key}`}>
        <path
          d={l.d}
          fill="none"
          stroke={l.color}
          strokeOpacity={l.strokeOpacity}
          strokeWidth={l.strokeWidth}
          strokeDasharray={l.dashArray}
          vectorEffect="non-scaling-stroke"
          style={{ animation: `dash 2.4s ease-out ${l.delay}s forwards`, strokeDashoffset: 800 }}
        />
        <circle cx={l.anchor.x} cy={l.anchor.y} r="4.5" fill={l.color} opacity="0.95" stroke="#15111A" strokeWidth="0.6" />
        {showConnector && (
          <line x1={l.anchor.x} y1={l.anchor.y} x2={la.x} y2={la.y} stroke={l.color} strokeOpacity="0.35" strokeWidth="0.6" strokeDasharray="1 2" vectorEffect="non-scaling-stroke" />
        )}
        <rect x={la.x - 2} y={la.y - labelH + 2} width={labelW} height={labelH} rx={2} fill="#15111A" fillOpacity="0.82" stroke={l.color} strokeOpacity="0.35" strokeWidth="0.5" />
        <text x={la.x + 2} y={la.y - 7} fill={l.color} fontSize={fs} fontFamily="'Courier Prime', monospace" style={{ textTransform: "uppercase", letterSpacing: "1.5px" }}>
          {l.label}
        </text>
      </g>
    );
  });
}

// Previous-scan pins clustered by proximity; each cluster shows one dot + up to 3 stacked labels.
export function PrevPinsLayer({ clusters, onOpenHub }) {
  return clusters.map((cluster) => {
    const { x: px, y: py, items, isEgg } = cluster;
    const n = items.length;
    const lineH = 10;
    const clusterKey = items.map((it) => it.city.toLowerCase()).join("|");
    return (
      <g key={`prev-${clusterKey}`} onClick={() => onOpenHub(items[0].city)} style={{ cursor: "pointer" }} data-testid={`map-pin-cluster-${clusterKey}`}>
        {isEgg && (
          <circle cx={px} cy={py} r="8" fill="none" stroke="#C8A96E" strokeWidth="1.5" opacity="0.9">
            <animate attributeName="r" values="6;12;6" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
          </circle>
        )}
        <circle cx={px} cy={py} r={n > 1 ? 4 : 3} fill={isEgg ? "#C8A96E" : "#A89C98"} opacity={isEgg ? 1 : 0.8} />
        {n > 1 && <circle cx={px} cy={py} r="6" fill="none" stroke="#A89C98" strokeOpacity="0.55" strokeWidth="0.8" />}
        {items.slice(0, 3).map((it, k) => {
          const label = it.city.toUpperCase();
          const fs = 8;
          const labelW = label.length * fs * 0.6 + 6;
          const rowY = py - 6 + k * lineH;
          return (
            <g key={`row-${it.city}`}>
              <rect x={px + 6 - 1} y={rowY - fs + 1} width={labelW} height={fs + 2} rx={1.5} fill="#15111A" fillOpacity="0.75" />
              <text x={px + 6} y={rowY} fill={it.easter_egg ? "#C8A96E" : "#A89C98"} fontSize={fs} fontFamily="'Courier Prime', monospace" style={{ letterSpacing: "1px" }}>
                {label}
              </text>
            </g>
          );
        })}
        {n > 3 && (
          <text x={px + 6} y={py - 6 + 3 * lineH} fill="#C8A96E" fontSize="7" fontFamily="'Courier Prime', monospace" style={{ letterSpacing: "1px" }}>
            {`+${n - 3} MORE`}
          </text>
        )}
      </g>
    );
  });
}

// The scanned city: glow halo, pulsing dot, and a centered label pill directly below the pin.
export function ActivePin({ cx, cy, city, glow, easterEgg, onOpenHub }) {
  const activeLabel = (city || "").toUpperCase();
  const fs = 11;
  const labelW = activeLabel.length * fs * 0.62 + 12;
  const labelH = fs + 6;
  const labelX = cx - labelW / 2;
  const labelY = cy + 14;
  return (
    <g onClick={() => onOpenHub(city)} style={{ cursor: "pointer" }} data-testid="map-pin-active">
      <circle cx={cx} cy={cy} r={easterEgg ? 60 : 40} fill="url(#pinGlow)" pointerEvents="none" />
      {easterEgg && (
        <>
          <circle cx={cx} cy={cy} r="18" fill="none" stroke="#C8A96E" strokeWidth="2">
            <animate attributeName="r" values="14;24;14" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.2;1" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <circle cx={cx} cy={cy} r="26" fill="none" stroke="#C8A96E" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="3 3">
            <animateTransform attributeName="transform" type="rotate" from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="8s" repeatCount="indefinite" />
          </circle>
        </>
      )}
      <circle cx={cx} cy={cy} r="8" fill={glow} filter="url(#softGlow)">
        <animate attributeName="r" values="8;12;8" dur="2.5s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r="3" fill="#ffffff" />
      <rect x={labelX} y={labelY} width={labelW} height={labelH} rx={3} fill="#15111A" fillOpacity="0.88" stroke="#C8A96E" strokeOpacity="0.45" strokeWidth="0.6" />
      <text x={cx} y={labelY + fs + 1} fill="#C8A96E" fontSize={fs} fontFamily="'Courier Prime', monospace" textAnchor="middle" style={{ letterSpacing: "2px" }}>
        {activeLabel}
      </text>
    </g>
  );
}
